import React from 'react'

import {
  AlertIcon,
  AlertReportedFillIcon,
  ArcadeIcon,
  AvatarFillIcon,
  BankAccountIcon,
  BitcoinLightningIcon,
  BlockIcon,
  BorrowIcon,
  CameraFillIcon,
  CardActiveIcon,
  CardAddIcon,
  CardBasicIcon,
  CardInactiveIcon,
  CheckIcon,
  ClearIcon,
  CommEmailIcon,
  DateIcon,
  DepositIcon,
  DocumentFillIcon,
  DrawIcon,
  FailedIcon,
  FamilyIcon,
  iconIdMapping,
  InternationalIcon,
  InvestingIcon,
  LocationFillIcon,
  LockLockedFillIcon,
  LockUnlockedFillIcon,
  LogoUsdIcon,
  NotificationsFillIcon,
  PendingIcon,
  QrIcon,
  RecurringAutomaticIcon,
  RoundUpsIcon,
  SavingsGoalIcon,
  SecurityAlertFillIcon,
  SecurityCheckFillIcon,
  SecurityLockFillIcon,
  SubtractIcon,
  TransferWireIcon,
} from '@arcade/web/icons/react'
import { IconMat, IconMatProps, PaddingInset } from '@cash-design-system/react'
import {
  FormBlockerElementLocalImageElement,
  FormBlockerElementLocalImageElementIcon,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'

import { MooncakeCVV } from './images/MooncakeCVV'

function withIconMat(IconComponent: ArcadeIcon<16 | 24 | 32> | ArcadeIcon<24 | 32>, props: IconMatProps = {}) {
  return () => (
    <IconMat {...props}>
      <IconComponent size={32} data-testid={IconComponent.name} />
    </IconMat>
  )
}

export const LocalImages: Partial<Record<keyof typeof FormBlockerElementLocalImageElementIcon, React.ComponentType>> = {
  [FormBlockerElementLocalImageElementIcon.ACTION_REQUIRED]: withIconMat(AlertIcon, {
    variant: 'danger',
  }),
  [FormBlockerElementLocalImageElementIcon.BANK]: withIconMat(BankAccountIcon),
  [FormBlockerElementLocalImageElementIcon.BELL]: withIconMat(NotificationsFillIcon),
  [FormBlockerElementLocalImageElementIcon.BORROW]: withIconMat(BorrowIcon),
  [FormBlockerElementLocalImageElementIcon.CALENDAR]: withIconMat(DateIcon),
  [FormBlockerElementLocalImageElementIcon.CAMERA]: withIconMat(CameraFillIcon),
  [FormBlockerElementLocalImageElementIcon.CARD_CHECKMARK]: withIconMat(CardActiveIcon),
  [FormBlockerElementLocalImageElementIcon.CARD_EXPIRED]: withIconMat(CardInactiveIcon),
  [FormBlockerElementLocalImageElementIcon.CARD_TOP_RIGHT_PLUS]: withIconMat(CardAddIcon),
  [FormBlockerElementLocalImageElementIcon.CARD]: withIconMat(CardBasicIcon),
  [FormBlockerElementLocalImageElementIcon.CLEAR]: withIconMat(ClearIcon, { variant: 'subtle' }),
  [FormBlockerElementLocalImageElementIcon.CVV]: MooncakeCVV,
  [FormBlockerElementLocalImageElementIcon.DASH]: withIconMat(SubtractIcon),
  [FormBlockerElementLocalImageElementIcon.DECLINED]: withIconMat(BlockIcon),
  [FormBlockerElementLocalImageElementIcon.DEPOSIT]: withIconMat(DepositIcon),
  [FormBlockerElementLocalImageElementIcon.DOCUMENT]: withIconMat(DocumentFillIcon),
  [FormBlockerElementLocalImageElementIcon.ELIGIBILITY_WARNING]: withIconMat(AlertIcon, {
    variant: 'inverse',
  }),
  [FormBlockerElementLocalImageElementIcon.ENVELOPE]: withIconMat(CommEmailIcon),
  [FormBlockerElementLocalImageElementIcon.FAILURE]: withIconMat(FailedIcon, {
    variant: 'danger',
  }),
  [FormBlockerElementLocalImageElementIcon.FAMILY]: withIconMat(FamilyIcon),
  [FormBlockerElementLocalImageElementIcon.GLOBE]: withIconMat(InternationalIcon),
  [FormBlockerElementLocalImageElementIcon.GOAL]: withIconMat(SavingsGoalIcon),
  [FormBlockerElementLocalImageElementIcon.GREEN_EXCLAMATION]: withIconMat(AlertIcon),
  [FormBlockerElementLocalImageElementIcon.INSTANT]: withIconMat(BitcoinLightningIcon),
  [FormBlockerElementLocalImageElementIcon.INVESTING_ROUND_UPS_INTRO_ILLUSTRATION]: withIconMat(RoundUpsIcon),
  [FormBlockerElementLocalImageElementIcon.INVESTING_ROUND_UPS]: withIconMat(RoundUpsIcon),
  [FormBlockerElementLocalImageElementIcon.INVESTING]: withIconMat(InvestingIcon),
  [FormBlockerElementLocalImageElementIcon.LOCATION]: withIconMat(LocationFillIcon),
  [FormBlockerElementLocalImageElementIcon.LOCK_IN_SHIELD]: withIconMat(SecurityLockFillIcon),
  [FormBlockerElementLocalImageElementIcon.LOCKED]: withIconMat(LockLockedFillIcon),
  [FormBlockerElementLocalImageElementIcon.LOGOMARK]: withIconMat(LogoUsdIcon),
  [FormBlockerElementLocalImageElementIcon.PENDING]: withIconMat(PendingIcon),
  [FormBlockerElementLocalImageElementIcon.PERSON]: withIconMat(AvatarFillIcon),
  [FormBlockerElementLocalImageElementIcon.QR_CODE]: withIconMat(QrIcon),
  [FormBlockerElementLocalImageElementIcon.RECURRING_DOUBLE_ARROW]: withIconMat(RecurringAutomaticIcon),
  [FormBlockerElementLocalImageElementIcon.RECURRING]: withIconMat(RecurringAutomaticIcon),
  [FormBlockerElementLocalImageElementIcon.SCRIBBLE]: withIconMat(DrawIcon),
  [FormBlockerElementLocalImageElementIcon.SECURITY_WARNING]: withIconMat(AlertIcon, { variant: 'danger' }),
  [FormBlockerElementLocalImageElementIcon.SUCCESS]: withIconMat(CheckIcon),
  [FormBlockerElementLocalImageElementIcon.UNLOCK]: withIconMat(LockUnlockedFillIcon),
  [FormBlockerElementLocalImageElementIcon.VERIFICATION_FAILED]: withIconMat(SecurityAlertFillIcon, {
    variant: 'danger',
  }),
  [FormBlockerElementLocalImageElementIcon.VERIFICATION]: withIconMat(SecurityCheckFillIcon),
  [FormBlockerElementLocalImageElementIcon.VERIFYING]: withIconMat(SecurityLockFillIcon),
  [FormBlockerElementLocalImageElementIcon.WARNING]: withIconMat(AlertReportedFillIcon),
  [FormBlockerElementLocalImageElementIcon.WIRE]: withIconMat(TransferWireIcon),
}

const LocalImageElement = ({ icon, local_icon: localIcon }: FormBlockerElementLocalImageElement) => {
  if (localIcon) {
    const icon = localIcon.arcade_id ? iconIdMapping[localIcon.arcade_id] : undefined
    if (!icon) {
      // If this error is logged either:
      //   - add entry to iconIdMapping for missing arcadeIconId
      //   - or if the icon id is wrong have the responsible server team change the value
      logger.error(new Error(`LocalImageElement no mapping found for the arcade icon id of ${localIcon.arcade_id}`))
      return null
    }

    const { Icon, size } = icon
    return <Icon size={size} data-testid={`LocalImageElement-${Icon.name}`} />
  } else if (icon) {
    const LocalImage = LocalImages?.[icon]

    if (!LocalImage) {
      logger.error(new Error(`LocalImageElement unsupported icon: ${icon}`))
      return null
    }

    return (
      <PaddingInset data-testid={`LocalImageElement-${icon}`}>
        <LocalImage />
      </PaddingInset>
    )
  } else {
    logger.error(new Error(`LocalImageElement missing icon data`))
    return null
  }
}

export default LocalImageElement
